/** Static copy only — no user-controlled strings. Safe for any HTTP status page. */

export type HttpStatusCopy = {
  title: string;
  description: string;
};

const DEFAULT_UNKNOWN: HttpStatusCopy = {
  title: 'Something went wrong',
  description: 'We could not complete this request. You can try again or return to the home page.',
};

export const HTTP_STATUS_COPY: Record<number, HttpStatusCopy> = {
  /** Informational redirect family — rarely shown as a full page; included for demos and static hosting. */
  300: {
    title: 'Multiple choices',
    description:
      'The resource is available in more than one form. Follow the link your application or server selected, or choose the correct location.',
  },
  400: {
    title: 'Bad request',
    description: 'The request could not be understood. Check the address or form and try again.',
  },
  401: {
    title: 'Unauthorized',
    description: 'You need to sign in to access this resource.',
  },
  403: {
    title: 'Forbidden',
    description: 'You do not have permission to access this page.',
  },
  404: {
    title: 'Page not found',
    description: 'The page you are looking for does not exist or has been moved.',
  },
  408: {
    title: 'Request timeout',
    description: 'The server took too long to respond. Please try again.',
  },
  429: {
    title: 'Too many requests',
    description: 'You have sent too many requests in a short time. Please wait and try again.',
  },
  500: {
    title: 'Server error',
    description: 'Something went wrong on our side. We are working to fix it.',
  },
  502: {
    title: 'Bad gateway',
    description: 'An upstream server returned an invalid response. Please try again later.',
  },
  503: {
    title: 'Service unavailable',
    description: 'The service is temporarily unavailable. Please try again shortly.',
  },
};

export function httpStatusCopyFor(code: number): HttpStatusCopy {
  return HTTP_STATUS_COPY[code] ?? DEFAULT_UNKNOWN;
}

/** Dedicated routes: `/maintenance`, `/server-down` — static copy for ops / CDN swap pages. */
export const MAINTENANCE_PAGE = {
  statusCode: 503,
  title: 'Under maintenance',
  description:
    'We are upgrading systems to serve you better. The site should be available again shortly. Thank you for your patience.',
} as const;

export const SERVER_DOWN_PAGE = {
  statusCode: 502,
  title: 'Service temporarily down',
  description:
    'Our systems are not responding as expected. The team has been alerted. Please try again in a few minutes.',
} as const;
